/**
 * SPEC-185 — contrato de error de autenticación (design.md §7 + handoff, sección
 * "State Management": `errorCode`).
 *
 * El backend emite tres códigos tipados desde el middleware `authenticate`:
 * `UNAUTHORIZED_DOMAIN`, `ACCOUNT_DISABLED` y `SESSION_EXPIRED`. `NETWORK_ERROR` es el
 * código que el FRONTEND deriva cuando no recibe ningún código tipado: fallo de red o
 * timeout real, y también los rechazos heredados de SPEC-175 (header ausente / token
 * inválido), que siguen respondiendo el cuerpo genérico anterior.
 */
export type AuthErrorCode =
  | 'UNAUTHORIZED_DOMAIN'
  | 'ACCOUNT_DISABLED'
  | 'SESSION_EXPIRED'
  | 'NETWORK_ERROR';

const CODIGOS_DEL_BACKEND: AuthErrorCode[] = [
  'UNAUTHORIZED_DOMAIN',
  'ACCOUNT_DISABLED',
  'SESSION_EXPIRED',
];

/** Copy exacto de los 4 banners de error del handoff. */
export const COPY_ERROR: Record<AuthErrorCode, { titulo: string; cuerpo: string }> = {
  UNAUTHORIZED_DOMAIN: {
    titulo: 'Cuenta no autorizada',
    cuerpo: 'Esta cuenta de Google no pertenece al dominio corporativo. Contacta al administrador.',
  },
  ACCOUNT_DISABLED: {
    titulo: 'Cuenta desactivada',
    cuerpo: 'Tu cuenta fue desactivada. Escribe a soporte para reactivarla.',
  },
  SESSION_EXPIRED: {
    titulo: 'Tu sesión expiró',
    cuerpo: 'Vuelve a iniciar sesión para continuar.',
  },
  NETWORK_ERROR: {
    titulo: 'No pudimos conectar',
    cuerpo: 'Revisa tu conexión e inténtalo de nuevo.',
  },
};

interface CuerpoErrorTipado {
  success?: boolean;
  error?: { code?: unknown; message?: unknown };
}

function esCodigoDelBackend(valor: unknown): valor is AuthErrorCode {
  return typeof valor === 'string' && (CODIGOS_DEL_BACKEND as string[]).includes(valor);
}

/**
 * Traduce una respuesta rechazada del backend al código de banner correspondiente.
 * Cualquier respuesta sin `error.code` reconocible (cuerpo genérico, HTML de un proxy,
 * JSON inválido) se trata como `NETWORK_ERROR`: es lo que el usuario puede accionar.
 */
export async function resolverCodigoError(response: Response): Promise<AuthErrorCode> {
  try {
    const cuerpo: CuerpoErrorTipado = await response.json();
    return esCodigoDelBackend(cuerpo?.error?.code) ? cuerpo.error!.code as AuthErrorCode : 'NETWORK_ERROR';
  } catch {
    return 'NETWORK_ERROR';
  }
}
